import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '../tokens/cn';
import { focusRing, transitionBase } from '../tokens/variants';

export interface SliderProps
  extends ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  size?: 'sm' | 'md';
}

const sizeMap = {
  sm: { track: 'h-1.5', thumb: 'h-4 w-4' },
  md: { track: 'h-2', thumb: 'h-4 w-4' },
};

export const Slider = forwardRef<
  ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(({ className, size = 'md', ...props }, ref) => {
  const { track, thumb } = sizeMap[size];
  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex w-full touch-none select-none items-center',
        'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          'relative w-full grow overflow-hidden rounded-tk-full',
          'bg-[var(--tk-surface-border-strong)]/60',
          track,
        )}
      >
        <SliderPrimitive.Range
          className={cn(
            'absolute h-full bg-[var(--tk-brand-bg)]',
            transitionBase,
          )}
        />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        className={cn(
          'block rounded-full cursor-grab active:cursor-grabbing',
          'bg-[var(--tk-brand-bg)]',
          'data-[disabled]:cursor-not-allowed',
          focusRing,
          transitionBase,
          thumb,
        )}
      />
    </SliderPrimitive.Root>
  );
});

Slider.displayName = 'Slider';
